import { FeaturesReady, useFeatureValue } from '@growthbook/growthbook-react';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  centsToDollarsNumber,
  displayUSDValueForRegion,
  expectUnreachable,
  getExperimentalTotalNanoPrice,
  getLocalizedPricing,
  getPriceForRateUnit,
  RateUnit,
} from '../../lib/util';
import * as types from '../../types';
import { miniCollarId, series3PlusCollarId, ultraCollarId } from '../../types';
import styles from './SubscriptionOptionDetails.module.scss';
import { showNanoVariant } from '../NanoBanner/NanoBanner';
import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../lib/growthbook';
import { getNanoReferralPetNameCookie } from '../../lib/util/nanoReferralPetNameCookie';
import { FiCountry, useLocale } from '../../contexts/LocaleContext';
import { useIsGift } from '../../lib/util/isGift';
import { Flex, Text } from '@mantine/core';
import FreeTrialLearnMoreModal from '../FreeTrialLearnMoreModal';
import * as events from '../../lib/analytics/events';
import { getFreeTrialDurationLabel } from '../../lib/subscription';
import useFreeTrialShippingPrice from '../../hooks/useFreeTrialShippingPrice';
import useFreeTrialSubscriptionPrice from '../../hooks/useFreeTrialSubscriptionPrice';
import { ReactComponent as IconWarrantyOutline } from '../../assets/images/icons/icon_warranty_outline.svg';

export interface SubscriptionOption {
  billingCadence: types.BillingCadence;
  billingPeriodsPerTerm: number;
  buyItMembership?: boolean;
  displayedRate: string;
  displayedRateUnit: string;
  freeTrial?: boolean;
  freeTrialDays?: number;
  name: string;
  /** @deprecated use `pricingInCents` instead */
  priceInCents: number;
  pricingInCents: types.PricingByCurrency;
  renewalMonths: number;
  renewalYears?: number | null;
  savings?: string | null;
  monthlyUpgradeSavings?: string | null;
  sku: string;
  upsellOnly?: boolean;
  valueLabel?: string | null;
  supportPlus?: boolean;
  couponExtensionMonths?: number | null;
  planSwitchCreditAppliedInCents?: number | null;
  planSwitchAmountChargedInCents?: number | null;
}

interface SubscriptionOptionButtonProps {
  subscriptionProduct: SubscriptionOption;
  renewalDate?: DateTime;
  expirationDate?: DateTime;
  pauseDate?: DateTime;
  disableSavingsDisplay?: boolean;
  overrideTitle?: string;
  overrideSubtitle?: string;
  pricingRateUnit?: RateUnit;
  renewScreens?: boolean;
  monthlyUpgrade?: boolean;
  showSubscriptionLengthDiscount?: boolean;
  prominentPricingDesign?: boolean;
  isPaused?: boolean;
  redemptionType?: types.RedemptionType;
  hasValidRedemptionCode?: boolean;
  isReferral?: boolean;
  productCategory?: types.ProductCategory;
  onStartFreeTrial?: () => void;
}

function renewalTitle(renewalMonths: number, prominentPricingDesign?: boolean, t?: TFunction) {
  if (!t) return '';
  if (renewalMonths % 12 === 0) {
    const years = Math.round(renewalMonths / 12);
    return t('subscription_years_year', { years });
  }

  if (renewalMonths === 1) {
    return prominentPricingDesign ? t('subscription_month_to_month') : t('subscription_monthly');
  }

  return t('subscription_months', { months: renewalMonths });
}

function elevatedNanoOfferDescription(subscription: SubscriptionOption, t: TFunction) {
  return t('plus_activation_fee');
}

function billingCadenceDescription(
  subscription: SubscriptionOption,
  country: FiCountry,
  currency: types.FiCurrency,
  t: TFunction,
  currencyFormatter: Intl.NumberFormat,
) {
  let pricePeriod = '';
  const priceInCents = getLocalizedPricing(subscription.pricingInCents, currency);
  const price = currencyFormatter.format(centsToDollarsNumber(priceInCents));
  if (subscription.renewalMonths === 1) {
    pricePeriod = t('subscription_price_per_month', { price });
  } else if (subscription.renewalMonths === 6) {
    pricePeriod = t('subscription_price_semi_annually', { price });
  } else if (subscription.renewalMonths % 12 === 0) {
    const years = Math.round(subscription.renewalMonths / 12);
    if (years === 1) {
      pricePeriod = t('subscription_price_annually', { price });
    } else if (years === 2) {
      pricePeriod = t('subscription_price_biennially', { price });
    } else {
      pricePeriod = t('subscription_price_every_years', { price, years });
    }
  } else {
    pricePeriod = t('subscription_price_every_months', { price, months: subscription.renewalMonths });
  }

  return t('billed_as_price_period', { pricePeriod });
}

function nextSignificantDateDescription(
  renewScreens: boolean,
  isPaused: boolean,
  renewalDate?: DateTime,
  expirationDate?: DateTime,
  pauseDate?: DateTime,
  t?: TFunction,
) {
  if (!t) return null;
  const DATE_FORMAT_TOKEN = 'LL/dd/yy, h:mma';
  if (renewScreens && expirationDate) {
    return (
      <div className={styles.description}>
        {t('plan_expires_on_date', { date: expirationDate.toFormat(DATE_FORMAT_TOKEN) })}
      </div>
    );
  }

  if (isPaused && renewalDate && pauseDate) {
    const pauseDateIsInFuture = pauseDate > DateTime.now();

    return (
      <div className={styles.description}>
        {pauseDateIsInFuture
          ? t('membership_will_pause_on_date', { date: renewalDate.toFormat(DATE_FORMAT_TOKEN) })
          : t('membership_will_resume_on_date', { date: renewalDate.toFormat(DATE_FORMAT_TOKEN) })}
      </div>
    );
  }

  if (renewalDate) {
    return (
      <div className={styles.description}>{t('renews_date', { date: renewalDate.toFormat(DATE_FORMAT_TOKEN) })}</div>
    );
  }
  return null;
}

function billingRateFragment(
  nanoVariantWithElevatedOffer: boolean,
  showTotalPriceFormat: boolean,
  country: FiCountry,
  currency: types.FiCurrency,
  currencyFormatter: Intl.NumberFormat,
  wholeNumberCurrencyFormatter: Intl.NumberFormat,
  displayRateUnit: string,
  displayRate: string,
  priceInCents: number,
  overrideSubtitle: string | undefined,
  renewalMonths: number,
  t: TFunction,
) {
  if (nanoVariantWithElevatedOffer) {
    return (
      <div className={styles.price}>
        <span className={styles.priceBold}>{displayRate + ' '}</span>
        <span className={classNames(styles.priceBold, styles.priceBoldCrossed)}>
          {currencyFormatter.format(centsToDollarsNumber(priceInCents))}
        </span>
      </div>
    );
  }

  if (showTotalPriceFormat) {
    // Calculate monthly rate for display
    const monthlyPriceInCents = Math.floor(priceInCents / renewalMonths);
    const monthlyPrice = wholeNumberCurrencyFormatter.format(centsToDollarsNumber(monthlyPriceInCents));
    return (
      <div className={styles.price}>
        <span className={styles.priceBold}>
          {wholeNumberCurrencyFormatter.format(centsToDollarsNumber(priceInCents))}
        </span>
        {renewalMonths > 1 && (
          <span className={styles.monthlyBreakdown}> {t('equal_to_price_per_month', { price: monthlyPrice })}</span>
        )}
      </div>
    );
  }

  return (
    <div className={styles.price}>
      {overrideSubtitle ? (
        <Text size={16} fw={600} lh={1.4}>
          {overrideSubtitle}
        </Text>
      ) : (
        <>
          <span className={styles.priceBold}>{displayUSDValueForRegion(country, currency, displayRate)}</span>
          {' / '}
          {displayRateUnit}
        </>
      )}
    </div>
  );
}

function billingCadenceDescriptionFragment(
  nanoVariantWithElevatedOffer: boolean,
  totalPriceSubscriptionEnabled: boolean,
  country: FiCountry,
  currency: types.FiCurrency,
  displayRateUnit: string,
  displayRate: string,
  subscriptionProduct: SubscriptionOption,
  t: TFunction,
  currencyFormatter: Intl.NumberFormat,
) {
  if (nanoVariantWithElevatedOffer) {
    return (
      <>
        <div className={styles.description}>{elevatedNanoOfferDescription(subscriptionProduct, t)}</div>
      </>
    );
  }

  if (totalPriceSubscriptionEnabled) {
    return (
      <div className={styles.description}>
        {' '}
        {'Equal to '}
        {displayUSDValueForRegion(country, currency, displayRate)}
        {' / '}
        {displayRateUnit}
      </div>
    );
  }

  return (
    <div className={styles.description}>
      {billingCadenceDescription(subscriptionProduct, country, currency, t, currencyFormatter)}
    </div>
  );
}

export function MonthlySubscriptionContent({
  subscriptionProduct,
  renewalDate,
  expirationDate,
  pauseDate,
  disableSavingsDisplay,
  overrideTitle,
  overrideSubtitle,
  pricingRateUnit = 'week',
  renewScreens = false,
  monthlyUpgrade = false,
  showSubscriptionLengthDiscount = false,
  prominentPricingDesign = false,
  isPaused = false,
  isReferral = false,
}: SubscriptionOptionButtonProps) {
  const isGift = useIsGift();
  const { t } = useTranslation();
  const { country, currency, currencyFormatter, wholeNumberCurrencyFormatter } = useLocale();
  const referralDiscountInCents = getReferralDiscountInCents(subscriptionProduct.renewalMonths, currency);
  const discountedPriceInCents =
    getLocalizedPricing(subscriptionProduct.pricingInCents, currency) - referralDiscountInCents;
  // Don't show nano ecomm test variant on in-app collar management screens
  const nanoEcomTestTwoEnabled =
    !window.location.pathname.startsWith('/subscription') &&
    !window.location.pathname.startsWith('/redeem') &&
    !window.location.pathname.startsWith('/vip') &&
    (showNanoVariant() || getFiGrowthBook().getFeatureValue('nano-ecom-test-2', false));

  const nanoVariantWithElevatedOffer =
    showNanoVariant() && getFiGrowthBook().getFeatureValue('nano-elevated-promo-offer', false);

  const totalPriceSubscriptionEnabled = getFiGrowthBook().getFeatureValue('ecom-total-price-option', false);

  // Show total price format for Canada users or when GrowthBook flag is enabled
  const showTotalPriceFormat = totalPriceSubscriptionEnabled || country === FiCountry.CA;

  const { unit: displayRateUnit, displayRate } = nanoVariantWithElevatedOffer
    ? getExperimentalTotalNanoPrice(subscriptionProduct)
    : getPriceForRateUnit(subscriptionProduct, pricingRateUnit, currency, currencyFormatter);
  const priceInCents = getLocalizedPricing(subscriptionProduct.pricingInCents, currency);

  let prepaidPeriodTitle: string | undefined =
    overrideTitle ?? renewalTitle(subscriptionProduct.renewalMonths, prominentPricingDesign, t);

  if (nanoEcomTestTwoEnabled && subscriptionProduct.renewalMonths === 1) {
    prepaidPeriodTitle = undefined;
  }

  if (showTotalPriceFormat && subscriptionProduct.renewalMonths === 1) {
    prepaidPeriodTitle = t('subscription_monthly');
  }

  const savingsAmount = monthlyUpgrade ? subscriptionProduct.monthlyUpgradeSavings : subscriptionProduct.savings;
  const savingsPill = savingsAmount && !disableSavingsDisplay && (
    <div className={styles.savingsPill}>{savingsAmount}</div>
  );

  const saleBadge = useFeatureValue('ecom-bfcm-badge', 0);
  const blackFridayDesign = saleBadge === 1;
  const cyberMondayDesign = saleBadge === 2;

  return (
    <div
      className={classNames(styles.container, {
        [styles.prominentPricingDesign]: prominentPricingDesign,
        [styles.blackFridayDesign]: blackFridayDesign,
        [styles.cyberMondayDesign]: cyberMondayDesign,
      })}
    >
      <div className={styles.column}>
        {subscriptionProduct.renewalMonths === 12 && <div className={styles.mostPopularPill}>{t('most_popular')}</div>}
        {prepaidPeriodTitle && (
          <div className={classNames(styles.row, styles.title)}>
            {prepaidPeriodTitle && (
              <div className={styles.billingCadence} style={{ fontSize: overrideTitle ? 20 : undefined }}>
                {prepaidPeriodTitle}
              </div>
            )}
            {prominentPricingDesign && savingsPill}
            {showTotalPriceFormat &&
              showSubscriptionLengthDiscount &&
              getSubscriptionLengthBasedDollarExperimentDiscount(
                subscriptionProduct,
                currency,
                wholeNumberCurrencyFormatter,
              ) !== null && (
                <div className={styles.couponExtension}>
                  <span className={styles.couponDescription}>
                    {t('save_amount_with_promo', {
                      amount: getSubscriptionLengthBasedDollarExperimentDiscount(
                        subscriptionProduct,
                        currency,
                        wholeNumberCurrencyFormatter,
                      ),
                    })}
                  </span>
                </div>
              )}
          </div>
        )}
        {isReferral ? (
          <>
            <div className={styles.price}>
              <span className={classNames(styles.priceBold, styles.priceBoldCrossedSameSize)}>
                {currencyFormatter.format(centsToDollarsNumber(priceInCents))}
              </span>{' '}
              <span className={styles.priceBold}>
                {currencyFormatter.format(centsToDollarsNumber(discountedPriceInCents))}
              </span>
            </div>
            <div className={styles.description}>
              {billingCadenceDescription(subscriptionProduct, country, currency, t, currencyFormatter)}
            </div>
          </>
        ) : prominentPricingDesign ? (
          // isUpgradePath || nanoVariantEnabled ? false : https://app.growthbook.io/features/ecom-prominent-pricing
          // All other parent components are not using the prominentPricingDesign flag.
          <div className={styles.price}>
            <span
              className={classNames(styles.priceBold, {
                [styles.monthToMonthPrice]: subscriptionProduct.renewalMonths === 1,
              })}
            >
              {currencyFormatter.format(centsToDollarsNumber(priceInCents))}
              {subscriptionProduct.renewalMonths === 1 && '/mo'}
            </span>{' '}
            {subscriptionProduct.renewalMonths > 1 && (
              <span className={styles.description}>
                {t('as_low_as_price_per_unit', {
                  price: displayUSDValueForRegion(country, currency, subscriptionProduct.displayedRate), // TODO: move away from this?
                  unit:
                    subscriptionProduct.displayedRateUnit === 'month' ? 'mo' : subscriptionProduct.displayedRateUnit,
                })}
              </span>
            )}
          </div>
        ) : (
          // This path is the only live path in the tertiary conditional.
          <>
            {billingRateFragment(
              nanoVariantWithElevatedOffer,
              showTotalPriceFormat,
              country,
              currency,
              currencyFormatter,
              wholeNumberCurrencyFormatter,
              displayRateUnit,
              displayRate,
              priceInCents,
              overrideSubtitle,
              subscriptionProduct.renewalMonths,
              t,
            )}
            {!isGift &&
              !showTotalPriceFormat &&
              billingCadenceDescriptionFragment(
                nanoVariantWithElevatedOffer,
                showTotalPriceFormat,
                country,
                currency,
                displayRateUnit,
                displayRate,
                subscriptionProduct,
                t,
                wholeNumberCurrencyFormatter,
              )}
          </>
        )}
        {nextSignificantDateDescription(renewScreens, isPaused, renewalDate, expirationDate, pauseDate, t)}
      </div>
      <Flex justify="flex-end" align="flex-start" sx={{ whiteSpace: 'nowrap' }}>
        {prominentPricingDesign ? (
          <>
            {showSubscriptionLengthDiscount &&
              getSubscriptionLengthBasedDollarExperimentDiscount(
                subscriptionProduct,
                currency,
                wholeNumberCurrencyFormatter,
              ) !== null && (
                <div className={styles.couponExtension}>
                  <span className={styles.description}>
                    {getSubscriptionLengthBasedDollarExperimentDiscount(
                      subscriptionProduct,
                      currency,
                      wholeNumberCurrencyFormatter,
                    )}{' '}
                    {t('off')}
                  </span>
                  <span className={styles.title}>{t('exclusive_offer')}</span>
                </div>
              )}
          </>
        ) : (
          <>
            {!showTotalPriceFormat &&
              showSubscriptionLengthDiscount &&
              getSubscriptionLengthBasedDollarExperimentDiscount(
                subscriptionProduct,
                currency,
                wholeNumberCurrencyFormatter,
              ) !== null && (
                <div className={styles.couponExtension}>
                  {!isGift && (
                    <span className={styles.title}>
                      {blackFridayDesign
                        ? t('black_fi_day_caps')
                        : cyberMondayDesign
                        ? t('cyber_monday_caps')
                        : t('exclusive_offer')}
                    </span>
                  )}
                  <span className={styles.description}>
                    {getSubscriptionLengthBasedDollarExperimentDiscount(
                      subscriptionProduct,
                      currency,
                      wholeNumberCurrencyFormatter,
                    )}
                    {nanoVariantWithElevatedOffer ? '' : ' ' + t('off')}
                  </span>
                </div>
              )}
          </>
        )}
      </Flex>
      {(renewScreens || isPaused) && (
        <div className={classNames(styles.column, styles.right)}>
          <span className={classNames(styles.valueLabel, styles.deactivated)}>
            {renewScreens ? t('canceled') : t('paused')}
          </span>
        </div>
      )}
    </div>
  );
}

function getReferralDiscountInCents(renewalMonths: number, currency?: types.FiCurrency): number {
  if (currency && currency === types.FiCurrency.CAD) {
    switch (renewalMonths) {
      case 6:
        return 2500;
      case 12:
        return 4000;
      case 24:
        return 6000;
      default:
        return 0;
    }
  } else {
    switch (renewalMonths) {
      case 6:
        return 1500;
      case 12:
        return 3000;
      case 24:
        return 5000;
      default:
        return 0;
    }
  }
}

export function YearlySubscriptionContent({
  subscriptionProduct,
  renewalDate,
  expirationDate,
  pauseDate,
  disableSavingsDisplay,
  overrideTitle,
  renewScreens = false,
  isPaused = false,
}: SubscriptionOptionButtonProps) {
  const { t } = useTranslation();
  const { country, currency, currencyFormatter } = useLocale();
  return (
    <div className={styles.container}>
      <div className={styles.column}>
        <div className={classNames(styles.row, styles.title)}>
          <div className={styles.billingCadence}>
            {overrideTitle ?? renewalTitle(subscriptionProduct.renewalMonths, undefined, t)}
          </div>
          {subscriptionProduct.savings && !disableSavingsDisplay && (
            <div className={styles.savingsPill}>{subscriptionProduct.savings}</div>
          )}
        </div>
        <div className={styles.price}>
          <span className={styles.priceBold}>
            {displayUSDValueForRegion(country, currency, subscriptionProduct.displayedRate)}
          </span>{' '}
          {'/'}
          {subscriptionProduct.displayedRateUnit}
        </div>
        <div className={styles.description}>
          {billingCadenceDescription(subscriptionProduct, country, currency, t, currencyFormatter)}
        </div>
        {nextSignificantDateDescription(renewScreens, isPaused, renewalDate, expirationDate, pauseDate, t)}
      </div>
      {renewScreens || isPaused ? (
        <div className={classNames(styles.column, styles.right)}>
          <span className={classNames(styles.valueLabel, styles.deactivated)}>
            {renewScreens ? t('canceled') : t('paused')}
          </span>
        </div>
      ) : (
        <div className={styles.column}></div>
      )}
    </div>
  );
}

export function BuyItSubscriptionContent({ subscriptionProduct }: SubscriptionOptionButtonProps) {
  const { t } = useTranslation();
  const { country, currency } = useLocale();
  return (
    <div className={styles.container}>
      <div className={styles.column}>
        <div className={classNames(styles.row, styles.title)}>
          <div className={styles.billingCadence}>{t('buy_it')}</div>
        </div>
        <div className={styles.price}>
          {displayUSDValueForRegion(country, currency, subscriptionProduct.displayedRate)}
        </div>
        <div className={styles.description}>{t('one_time_payment')}</div>
      </div>
      <div className={classNames(styles.column, styles.right)}>
        <div className={styles.supportPlusWrapper}>
          {subscriptionProduct.supportPlus && (
            <>
              <div className={classNames(styles.supportLabel)}>{t('support_plus_included')}</div>
              <div className={classNames(styles.supportDescription)}>{t('prioritized_phone_support')}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function RedemptionSubscriptionContent({
  subscriptionProduct,
  hasValidRedemptionCode,
  redemptionType,
}: SubscriptionOptionButtonProps) {
  const { t } = useTranslation();
  const siteConfig = useSelector((state: types.AppState) => state.config.siteConfig);
  const { currency, currencyFormatter } = useLocale();

  // Check if this is a GwP free trial
  const isGwPTrial = redemptionType === 'gwp_free_trial';

  const gwpFreeTrialMonths = siteConfig.gwpFreeTrialMonths;

  const title = renewalTitle(subscriptionProduct.renewalMonths).toUpperCase();

  const priceInCents = getLocalizedPricing(subscriptionProduct.pricingInCents, currency);

  const redemptionBillingRateFragment = (
    <div className={styles.price}>
      {hasValidRedemptionCode ? (
        <>
          <span className={classNames(styles.priceBold, styles.priceBoldCrossed)}>
            {currencyFormatter.format(centsToDollarsNumber(priceInCents))}
          </span>
          <span className={styles.priceBold}>{currencyFormatter.format(0)}</span>
        </>
      ) : (
        <span className={classNames(styles.priceBold)}>
          {currencyFormatter.format(centsToDollarsNumber(priceInCents))}
        </span>
      )}
    </div>
  );
  const redemptionBillingCadenceDescriptionFragment = (
    <div className={styles.description}>{t('plus_activation_fee')}</div>
  );

  return (
    <div className={classNames(styles.container, styles.redemptionContainer)}>
      <div className={styles.column}>
        {redemptionType !== 'gwp_free_trial' && (
          <div className={classNames(styles.row, styles.title)}>
            <div className={styles.billingCadence}>{title}</div>
          </div>
        )}
        {redemptionBillingRateFragment}
        {redemptionBillingCadenceDescriptionFragment}
      </div>
      <div className={classNames(styles.column, styles.right)}>
        <div className={styles.couponExtension}>
          <span className={styles.title}>
            {hasValidRedemptionCode
              ? t('exclusive_offer_applied')
              : isGwPTrial
              ? t('apply_code_at_checkout_for_months_free', { months: gwpFreeTrialMonths })
              : t('apply_code_at_checkout_for_amount_off', {
                  amount: currencyFormatter.format(99),
                })}
          </span>
        </div>
      </div>
    </div>
  );
}

interface FreeTrialSubscriptionContentProps {
  freeTrialDays: number;
  productCategory: types.ProductCategory | undefined;
  onStartFreeTrial: (() => void) | undefined;
}

export function FreeTrialSubscriptionContent({
  freeTrialDays,
  productCategory,
  onStartFreeTrial,
}: FreeTrialSubscriptionContentProps) {
  const { t } = useTranslation();
  const collarProductId =
    productCategory === types.ProductCategory.MINI
      ? miniCollarId
      : productCategory === types.ProductCategory.COLLAR_KIT
      ? series3PlusCollarId
      : productCategory === types.ProductCategory.ULTRA
      ? ultraCollarId
      : null;

  const shippingPrice = useFreeTrialShippingPrice();
  const subscriptionPrice = useFreeTrialSubscriptionPrice(collarProductId);

  const descriptionKey =
    productCategory === types.ProductCategory.MINI
      ? 'free_trial_description_mini'
      : productCategory === types.ProductCategory.COLLAR_KIT
      ? 'free_trial_description_series3'
      : undefined;

  return (
    <div className={styles.container}>
      <div className={styles.column}>
        <div className={classNames(styles.row, styles.title, styles.freeTrialTitleRow)}>
          <div className={styles.billingCadence}>
            {t('free_trial_title', { duration: getFreeTrialDurationLabel(freeTrialDays) })}
          </div>
          <div className={styles.premiumPreOwnedBadge}>
            <IconWarrantyOutline />
            {t('risk_free_for_30_days')}
          </div>
        </div>
        {descriptionKey && shippingPrice && (
          <div className={styles.freeTrialDescription}>{t(descriptionKey, { shippingPrice })}</div>
        )}
        {shippingPrice && subscriptionPrice && (
          <FreeTrialLearnMoreModal
            trigger={
              <span className={styles.learnMoreUppercase} onClick={(e) => e.stopPropagation()}>
                {t('free_trial_learn_more')} {'→'}
              </span>
            }
            onStartFreeTrial={() => {
              events.startFreeTrialClickedFromLearnMoreModal();
              onStartFreeTrial?.();
            }}
            onOpen={() => events.freeTrialLearnMoreClickedFromSubscriptionOption()}
            shippingPrice={shippingPrice}
            subscriptionPrice={subscriptionPrice}
          />
        )}
      </div>
    </div>
  );
}

export function SubscriptionOverrideContent() {
  const { t } = useTranslation();
  const { currencyFormatter } = useLocale();
  return (
    <div className={styles.container}>
      <div className={styles.column}>
        <div className={classNames(styles.row, styles.title)}>
          <div className={styles.billingCadence}>{t('subscription_override')}</div>
        </div>
        <div className={styles.price}>{currencyFormatter.format(0)}</div>
        <div className={styles.description}>{t('renews_indefinitely')}</div>
      </div>
      <div className={styles.column}></div>
    </div>
  );
}

export function LifetimeSubscriptionContent() {
  const { t } = useTranslation();
  return (
    <div className={styles.container}>
      <div className={styles.column}>
        <div className={classNames(styles.row, styles.title)}>
          <div className={styles.billingCadence}>{t('lifetime_membership')}</div>
        </div>
        <div className={styles.price}></div>
        <div className={styles.description}>{t('renews_indefinitely')}</div>
      </div>
      <div className={styles.column}></div>
    </div>
  );
}

export default function SubscriptionOptionButton({
  disableSavingsDisplay,
  renewalDate,
  expirationDate,
  pauseDate,
  subscriptionProduct,
  overrideTitle,
  overrideSubtitle,
  renewScreens,
  monthlyUpgrade = false,
  showSubscriptionLengthDiscount = false,
  prominentPricingDesign = false,
  isPaused = false,
  redemptionType = null,
  hasValidRedemptionCode = false,
  isReferral = false,
  productCategory,
  onStartFreeTrial,
}: SubscriptionOptionButtonProps) {
  // This can be undefined if we're not wrapped in a GrowthBookProvider. We only use the GrowthBookProvider in
  // the main store flow so in-app webviews won't have this.
  const growthBookEnabled = useIsGrowthBookEnabled();

  if (subscriptionProduct.freeTrial && subscriptionProduct.freeTrialDays) {
    return (
      <FreeTrialSubscriptionContent
        freeTrialDays={subscriptionProduct.freeTrialDays}
        productCategory={productCategory}
        onStartFreeTrial={onStartFreeTrial}
      />
    );
  } else if (redemptionType) {
    return (
      <RedemptionSubscriptionContent
        subscriptionProduct={subscriptionProduct}
        hasValidRedemptionCode={hasValidRedemptionCode}
        redemptionType={redemptionType}
      />
    );
  } else if (subscriptionProduct.buyItMembership) {
    return <BuyItSubscriptionContent subscriptionProduct={subscriptionProduct} />;
  } else if (subscriptionProduct.billingCadence === types.BillingCadence.Month) {
    const monthlySubscriptionContent = (
      <MonthlySubscriptionContent
        disableSavingsDisplay={disableSavingsDisplay}
        renewalDate={renewalDate}
        expirationDate={expirationDate}
        pauseDate={pauseDate}
        overrideTitle={overrideTitle}
        overrideSubtitle={overrideSubtitle}
        subscriptionProduct={subscriptionProduct}
        pricingRateUnit={'week'}
        renewScreens={renewScreens}
        monthlyUpgrade={monthlyUpgrade}
        showSubscriptionLengthDiscount={showSubscriptionLengthDiscount}
        prominentPricingDesign={prominentPricingDesign}
        isPaused={isPaused}
        isReferral={isReferral}
      />
    );

    return growthBookEnabled ? <FeaturesReady>{monthlySubscriptionContent}</FeaturesReady> : monthlySubscriptionContent;
  } else if (subscriptionProduct.billingCadence === types.BillingCadence.Year) {
    return (
      <YearlySubscriptionContent
        disableSavingsDisplay={disableSavingsDisplay}
        renewalDate={renewalDate}
        expirationDate={expirationDate}
        pauseDate={pauseDate}
        overrideTitle={overrideTitle}
        subscriptionProduct={subscriptionProduct}
        renewScreens={renewScreens}
        isPaused={isPaused}
      />
    );
  } else if (subscriptionProduct.billingCadence === types.BillingCadence.Week) {
    // We don't support weekly subscriptions yet as they are currently only used by suplement subscriptions
    return null;
  } else {
    expectUnreachable(subscriptionProduct.billingCadence);
    return null;
  }
}

function getSubscriptionLengthBasedDollarExperimentDiscount(
  subscription: SubscriptionOption,
  currency: types.FiCurrency,
  currencyFormatter: Intl.NumberFormat,
): string | null {
  const showNanoOffer = showNanoVariant() || !!getNanoReferralPetNameCookie();

  const elevatedNanoOffer = getFiGrowthBook().getFeatureValue<boolean | null>('nano-elevated-promo-offer', false);

  if (showNanoOffer && elevatedNanoOffer) {
    return [6, 12, 24].includes(subscription.renewalMonths) ? `${currencyFormatter.format(99)} off` : null;
  }

  // TODO: calculate these properly: https://app.asana.com/1/553833434016848/project/1207222017559586/task/1211998760849818?focus=true
  if (currency && currency === types.FiCurrency.CAD) {
    switch (subscription.renewalMonths) {
      case 6:
        return currencyFormatter.format(65);
      case 12:
        return currencyFormatter.format(100);
      case 24:
        return currencyFormatter.format(120);
      default:
        return null;
    }
  } else {
    switch (subscription.renewalMonths) {
      case 6:
        return currencyFormatter.format(35);
      case 12:
        return currencyFormatter.format(50);
      case 24:
        return currencyFormatter.format(70);
      default:
        return null;
    }
  }
}
